import React, { useState, useEffect, useCallback } from 'react';
import { Download, Copy, Check, Instagram, Upload, RefreshCw, Image, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ── Type label helper ─────────────────────────────────────────────────────────
function typeLabel(type) {
  const map = {
    vaga_semana: 'Vaga da Semana',
    dica_candidatos: 'Dica para Candidatos',
    empresa_confia: 'Empresa que Confia',
    numero_semana: 'Número da Semana',
  };
  return map[type] || type;
}

function statusBadge(status) {
  const cfg = {
    pending_approval: { bg: 'bg-yellow-500/20 text-yellow-300', label: 'Por aprovar' },
    approved: { bg: 'bg-green-500/20 text-green-300', label: 'Aprovado' },
    published: { bg: 'bg-blue-500/20 text-blue-300', label: 'Publicado' },
  };
  const c = cfg[status] || { bg: 'bg-gray-500/20 text-gray-300', label: status };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.bg}`}>
      {c.label}
    </span>
  );
}

// ── Bio card ─────────────────────────────────────────────────────────────────
function BioCard({ bio }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(bio);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Bio copiada!');
  };
  return (
    <div className="bg-[#0d2255] border border-[#3D63F5]/30 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold text-sm">Bio recomendada</h3>
        <button
          data-testid="bio-copy-btn"
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-[#3D63F5] hover:text-[#F2B705] transition-colors font-semibold"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copiado!' : 'Copiar'}
        </button>
      </div>
      <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{bio}</p>
    </div>
  );
}

// ── Logo item ─────────────────────────────────────────────────────────────────
function LogoItem({ logo }) {
  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = `${BACKEND}${logo.url}`;
    a.download = logo.key;
    a.click();
  };
  return (
    <div className="bg-[#0d2255] border border-[#3D63F5]/30 rounded-xl p-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#0B1E44] flex items-center justify-center border border-[#3D63F5]/40">
          <Image size={18} className="text-[#3D63F5]" />
        </div>
        <span className="text-white text-sm font-medium">{logo.label}</span>
      </div>
      <button
        data-testid={`logo-download-${logo.key}`}
        onClick={handleDownload}
        className="flex items-center gap-1.5 text-xs text-[#F2B705] hover:opacity-80 transition-opacity font-semibold"
      >
        <Download size={14} /> Download
      </button>
    </div>
  );
}

// ── Highlight cover item ──────────────────────────────────────────────────────
function CoverItem({ cover }) {
  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = `${BACKEND}${cover.url}`;
    a.download = cover.filename;
    a.click();
  };
  return (
    <div className="bg-[#0d2255] border border-[#3D63F5]/30 rounded-xl p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded bg-[#3D63F5]/20 flex items-center justify-center">
          <Instagram size={16} className="text-[#3D63F5]" />
        </div>
        <span className="text-white text-sm font-medium">{cover.label}</span>
      </div>
      <button onClick={handleDownload}
        className="text-xs text-[#F2B705] hover:opacity-80 font-semibold flex items-center gap-1">
        <Download size={13} /> Download
      </button>
    </div>
  );
}

// ── Profile preview grid ──────────────────────────────────────────────────────
function ProfilePreview({ posts, onItemClick }) {
  if (!posts || posts.length === 0) {
    return (
      <div className="grid grid-cols-3 gap-1">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i}
            className="aspect-square bg-[#0d2255] rounded flex items-center justify-center border border-[#3D63F5]/20">
            <Image size={24} className="text-[#3D63F5]/30" />
          </div>
        ))}
      </div>
    );
  }
  const grid = [...posts].slice(0, 9);
  while (grid.length < 9) grid.push(null);

  return (
    <div className="grid grid-cols-3 gap-1">
      {grid.map((post, i) => (
        <button
          key={i}
          onClick={() => post && onItemClick(post)}
          className="aspect-square rounded overflow-hidden border border-[#3D63F5]/20 hover:border-[#F2B705]/60 transition-colors group relative"
          data-testid={`profile-grid-item-${i}`}
        >
          {post ? (
            <>
              <img
                src={`${BACKEND}${post.slide_urls[0]}`}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                <ChevronRight size={20} className="text-white opacity-0 group-hover:opacity-100" />
              </div>
            </>
          ) : (
            <div className="w-full h-full bg-[#0d2255] flex items-center justify-center">
              <Image size={20} className="text-[#3D63F5]/30" />
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Next week schedule ────────────────────────────────────────────────────────
function NextWeekSchedule({ items }) {
  const days = { monday: '2ª', wednesday: '4ª', friday: '6ª' };
  const byDay = {};
  (items || []).forEach(i => {
    if (!byDay[i.day]) byDay[i.day] = [];
    byDay[i.day].push(i);
  });

  return (
    <div className="space-y-2">
      {['monday', 'wednesday', 'friday'].map(day => {
        const dayItems = byDay[day] || [];
        const formats = dayItems.map(i => i.format);
        const mainItem = dayItems.find(i => i.format === 'post') || dayItems[0];
        return (
          <div key={day} className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-[#0d2255] border border-[#3D63F5]/20">
            <span className="text-[#F2B705] font-extrabold text-sm w-6">{days[day]}</span>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">
                {mainItem ? typeLabel(mainItem.type) : <span className="text-gray-500">—</span>}
              </p>
              {mainItem && (
                <p className="text-gray-400 text-xs">
                  {formats.includes('post') ? 'Post' : ''}{formats.includes('post') && formats.includes('story') ? ' + ' : ''}{formats.includes('story') ? `${dayItems.filter(i => i.format === 'story').length} stor${dayItems.filter(i => i.format === 'story').length === 1 ? 'y' : 'ies'}` : ''}
                </p>
              )}
            </div>
            {mainItem && statusBadge(mainItem.status)}
          </div>
        );
      })}
    </div>
  );
}

// ── Upload cover modal ────────────────────────────────────────────────────────
function UploadCoverModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      const renamed = new File([file], label ? `${label.replace(/\s+/g, '_')}.${file.name.split('.').pop()}` : file.name, { type: file.type });
      form.append('file', renamed);
      const { data } = await api.post('/weekly-content/highlight-covers/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Capa carregada!');
      onSuccess(data);
    } catch {
      toast.error('Erro ao carregar capa.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <form onSubmit={handleSubmit}
        className="bg-[#0B1E44] border border-[#3D63F5]/40 rounded-2xl p-6 w-full max-w-md space-y-4">
        <h3 className="text-white font-bold text-lg">Carregar capa de destaque</h3>
        <p className="text-gray-400 text-sm">Formato: PNG ou JPG, 1080×1920px</p>
        <input
          type="text"
          placeholder="Nome da capa (ex: Vagas)"
          value={label}
          onChange={e => setLabel(e.target.value)}
          className="w-full bg-[#0d2255] border border-[#3D63F5]/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#3D63F5]"
        />
        <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])}
          className="text-gray-300 text-sm" required />
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-[#3D63F5]/40 text-gray-300 text-sm hover:border-[#3D63F5] transition-colors">
            Cancelar
          </button>
          <button type="submit" disabled={uploading}
            className="flex-1 py-2.5 rounded-xl bg-[#3D63F5] text-white font-bold text-sm hover:bg-[#2d53e5] transition-colors disabled:opacity-50">
            {uploading ? 'A carregar...' : 'Carregar'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminInstagramKit() {
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  const loadKit = useCallback(async () => {
    try {
      const { data } = await api.get('/weekly-content/kit');
      setKit(data);
    } catch {
      toast.error('Erro ao carregar o kit.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKit(); }, [loadKit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={24} className="text-[#3D63F5] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12" data-testid="instagram-kit-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-white">Kit Instagram & Logótipos</h1>
          <p className="text-gray-400 text-sm mt-1">Todos os activos da marca Trabadju para o Instagram.</p>
        </div>
        <a href="/admin/content-studio"
          className="flex items-center gap-2 text-sm text-[#3D63F5] hover:text-[#F2B705] transition-colors font-semibold">
          Agente Semanal <ChevronRight size={16} />
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Próxima publicação */}
          <section>
            <h2 className="text-white font-bold text-base mb-3">
              Próxima publicação
              {kit?.next_monday && (
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  Semana de {new Date(kit.next_monday + 'T12:00:00').toLocaleDateString('pt-PT', { day: 'numeric', month: 'long' })}
                </span>
              )}
            </h2>
            {kit?.next_week_items?.length > 0 ? (
              <NextWeekSchedule items={kit.next_week_items} />
            ) : (
              <div className="bg-[#0d2255] border border-dashed border-[#3D63F5]/30 rounded-2xl p-6 text-center">
                <p className="text-gray-400 text-sm">Ainda não há conteúdo gerado para a próxima semana.</p>
                <a href="/admin/content-studio"
                  className="mt-3 inline-block text-[#3D63F5] text-sm font-semibold hover:text-[#F2B705]">
                  Ir para o Agente Semanal →
                </a>
              </div>
            )}
          </section>

          {/* Bio recomendada */}
          {kit?.bio && <BioCard bio={kit.bio} />}

          {/* Capas de destaques */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-bold text-base">Capas de Destaques (Stories)</h2>
              <button
                data-testid="upload-cover-btn"
                onClick={() => setShowUpload(true)}
                className="flex items-center gap-1.5 text-xs text-[#3D63F5] hover:text-[#F2B705] transition-colors font-semibold"
              >
                <Upload size={13} /> Carregar capa
              </button>
            </div>
            {kit?.highlight_covers?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {kit.highlight_covers.map(c => <CoverItem key={c.filename} cover={c} />)}
              </div>
            ) : (
              <div className="bg-[#0d2255] border border-dashed border-[#3D63F5]/30 rounded-xl p-5 text-center">
                <p className="text-gray-400 text-sm mb-2">Ainda não há capas carregadas.</p>
                <p className="text-gray-500 text-xs">Clica em "Carregar capa" para adicionar as tuas capas 1080×1920.</p>
              </div>
            )}
          </section>

          {/* Logótipos */}
          <section>
            <h2 className="text-white font-bold text-base mb-3">Logótipos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(kit?.logos || []).map(l => <LogoItem key={l.key} logo={l} />)}
            </div>
          </section>
        </div>

        {/* Right column — Profile preview */}
        <div className="space-y-4">
          <div>
            <h2 className="text-white font-bold text-base mb-3">Pré-visualização do perfil</h2>
            {/* Instagram-style profile header */}
            <div className="bg-[#0d2255] border border-[#3D63F5]/30 rounded-2xl p-4 mb-3">
              <div className="flex items-center gap-3 mb-3">
                <img
                  src={`${BACKEND}/api/weekly-content/logo-assets/perfil.png`}
                  alt="Trabadju"
                  className="w-14 h-14 rounded-full object-cover border-2 border-[#3D63F5]"
                />
                <div>
                  <p className="text-white font-bold text-sm">trabadju.cv</p>
                  <p className="text-gray-400 text-xs">Emprego em Cabo Verde</p>
                </div>
              </div>
              <div className="grid grid-cols-3 text-center text-xs border-t border-[#3D63F5]/20 pt-3">
                <div>
                  <p className="text-white font-bold">{kit?.recent_posts?.length || 0}</p>
                  <p className="text-gray-400">publicações</p>
                </div>
                <div>
                  <p className="text-white font-bold">—</p>
                  <p className="text-gray-400">seguidores</p>
                </div>
                <div>
                  <p className="text-white font-bold">—</p>
                  <p className="text-gray-400">seguindo</p>
                </div>
              </div>
            </div>
            <ProfilePreview posts={kit?.recent_posts || []} onItemClick={setSelectedPost} />
          </div>
        </div>
      </div>

      {/* Image lightbox */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setSelectedPost(null)}>
          <div className="max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <img
              src={`${BACKEND}${selectedPost.slide_urls[0]}`}
              alt={selectedPost.title}
              className="w-full rounded-2xl"
            />
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-white font-bold text-sm">{selectedPost.title}</p>
                <p className="text-gray-400 text-xs">{typeLabel(selectedPost.type)}</p>
              </div>
              {statusBadge(selectedPost.status)}
            </div>
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <UploadCoverModal
          onClose={() => setShowUpload(false)}
          onSuccess={(cover) => {
            setKit(prev => ({
              ...prev,
              highlight_covers: [...(prev?.highlight_covers || []), cover],
            }));
            setShowUpload(false);
          }}
        />
      )}
    </div>
  );
}
